import { createSlice, configureStore } from '@reduxjs/toolkit';
import Product from '../components/Product/Product';

const initialVisualizerState = { 
    seriesData: {},
    surfaceList : [],
    selectedSurface: {type:"FLOOR"},
    specifier:"",
    selString:"-1",
    newSceneLoaded:false,

    productsBySurfaceType:{},

    appliedFilters:[],
    floorPlanData:[],
    floorPlanSteps:[],
    stepsData:[],

    activeCommunity:null,
    activeFloorPlanData: null,
    activeFloorPlanSteps:[],
    activeStep:null,

    activePackage: null,
    
    shareData:{
        specifier:"",
        sel:-1,
        url:"",
        message:"Check out this design I made using the SeaGate Visualizer!"
    }
};

const visualizerSlice = createSlice({
  name: "visualizer",
  initialState: initialVisualizerState,
  reducers: {
    // Visualizer base states
    setSeriesData(state, action){
      state.seriesData[action.payload.seriesId] = action.payload.data;
    },
    
    setSurfaceList(state, action) {
      state.surfaceList = action.payload;
    },

    setSelectedSurface(state, action) {
      state.selectedSurface = action.payload;
    },

    setSpecifier(state, action) {
      state.specifier = action.payload;
    },

    setSelString(state, action) {
      state.selString = action.payload;
    },

    setNewSceneLoaded(state, action) {
      state.newSceneLoaded = action.payload;
    },

    //-------------------------------------

    // Applied filters state handlers
    addAppliedFilter(state, action) {
      var filter = action.payload;
      state.appliedFilters = [...state.appliedFilters, filter];
    },

    removeAppliedFilter(state, action) {
      state.appliedFilters = state.appliedFilters.filter(
        (f) => !(f.name === action.payload.name && f.value === action.payload.value)
      );
    },

    clearAppliedFilterByName(state, action){
      state.appliedFilters = state.appliedFilters.filter(
        (f) => f.name !== action.payload.name
      );
    },

    resetAppliedFilters(state, action){
      state.appliedFilters = [];
    },


    //------------------------------------
    setProductsBySurfaceType(state, action){
      state.productsBySurfaceType[action.payload.surface] = action.payload.data;
    }, 

    // Set floorplan data
    setFloorPlanData(state, action){
      state.floorPlanData = action.payload;
    },


    setActiveCommunity(state, action){
      state.activeCommunity = action.payload;
    }, 

    // Set active floorplan from floorplan ID
    setActiveFloorplanFromID(state, action){
      state.activeFloorPlanData = state.floorPlanData.filter(plan => plan.id === action.payload)[0];
    }, 
    
    // Set active floorplan with a data object
    setActiveFloorplanData(state, action){
      state.activeFloorPlanData = action.payload;
    }, 

    // Update active floorplan data with updated sel strings
    setAreaSelString(state, action){
      state.activeFloorPlanData[state.activeStep.area+"Sel"] = action.payload;
    },

    //------------------------------------

    // Load floorplan steps data
    setFloorPlanSteps(state, action){
      state.floorPlanSteps = action.payload;
    },

    // Set Steps data
    setStepsData(state, action){
      state.stepsData = action.payload;
    },

    // Set active floorplan steps from floorplan id
    setActiveFloorplanStepsFromID(state, action){
      let steps = state.floorPlanSteps
      .filter(step => step.plan_id === action.payload)
      .map(step => step.step_id);

      state.activeFloorPlanSteps = state.stepsData.filter(step => steps.includes(step.id));

      //renames Perimeter Cabinets to Cabinets if Island Cabinets does not exist
      let hasIslandCabinets = state.activeFloorPlanSteps.some(step => step.step === "Island Cabinets");

      if (!hasIslandCabinets) {
          state.activeFloorPlanSteps = state.activeFloorPlanSteps.map(step => 
              step.step === "Perimeter Cabinets" ? { ...step, step: "Cabinets" } : step
          );
      }

      state.activeStep = state.activeFloorPlanSteps[0];

    },

    updateActiveFloorplanSteps(state, action) {
      // Get the array of IDs to remove
      const idsToRemove = action.payload;
      // Filter out the objects whose id matches any ID in the idsToRemove array
      state.activeFloorPlanSteps = state.activeFloorPlanSteps.filter(
          step => !idsToRemove.includes(step.id)
      );
    },
  
  

    // Load active floorplan steps from a data object
    setActiveFloorplanStepsFromData(state, action){
      state.activeFloorPlanSteps = action.payload;
      state.activeStep = state.activeFloorPlanSteps[0];
    },

    // Update floorplan steps with selected products
    setSelection(state, action){
      state.activeStep.selection = action.payload;
      state.activeFloorPlanSteps.forEach(step => {
        if(step.id === state.activeStep.id){
          step.selection = action.payload;
        }
      })
    },

    // Update secondary selection
    setSecondarySelection(state, action){
      state.activeStep.secondarySelection = action.payload;
      state.activeFloorPlanSteps.forEach(step => {
        if(step.id === state.activeStep.id){
          step.secondarySelection = action.payload;
        }
      })
    },

    // Set active step from data object
    setActiveStep(state, action){
      state.activeStep = action.payload;
    },

    // Set active package
    setActivePackage(state, action){
      state.activePackage = action.payload;
    },

    //-----------------------------------

    // Set share data to be used from social share options & email
    setShareData(state,action){
      state.shareData = action.payload;
    }

  },
});


const initialUIState = {
  vizLoading: true,
  popUpModalActive : false,
  shareModalActive : false,
  helpModalActive : false,
  feedbackModalActive : false,
  emailModalActive : false,
  sendToSeagateModalStatus : false,
  sceneModalActive:false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUIState,
  reducers: {

    setVizLoading(state, action) {
      state.vizLoading = action.payload;
    },
    setShareModalActive(state, action){
      state.shareModalActive = action.payload;
    },
    setHelpModalActive(state, action){
      state.helpModalActive = action.payload;
    },
    setFeedbackModalActive(state, action){
      state.feedbackModalActive = action.payload;
    },
    setPopUpModalActive(state, action){
      state.popUpModalActive = action.payload;
    },
    setEmailModalActive(state, action){
      state.emailModalActive = action.payload;
    },
    setSendToSeagateModalActive(state, action){
      state.sendToSeagateModalStatus = action.payload;
    },
    setSceneModalActive(state, action){
      state.sceneModalActive = action.payload;
    }
  },
});

const store = configureStore({
  reducer: {
    visualizer: visualizerSlice.reducer,
    ui: uiSlice.reducer
  }
});

export const visualizerActions = visualizerSlice.actions;
export const uiActions = uiSlice.actions;

export default store;